import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import App from './App';
import ErrorBoundary from './components/ErrorBoundary';
import { ShareProvider } from './context/ShareProvider';
import { initMonitoring } from './utils/monitoring';
import { bootstrapLocale } from './utils/localeBootstrap';
import { removeBootSplash, shouldKeepBootSplash } from './utils/bootSplash';
import { syncWebAppManifest } from './utils/syncWebAppManifest';
import './i18n';
import './index.css';
import './components/miniwebsite/miniWebsiteLayout.css';
// react-hot-toast Toaster
import { Toaster } from 'react-hot-toast';

// Align PWA/shortcut manifest with current host + path before React mounts
// (critical for Add to Home Screen / browser shortcuts).
syncWebAppManifest();

// Initialize lightweight client monitoring (errors + web-vitals)
initMonitoring();

function renderApp() {
  createRoot(document.getElementById('root')!).render(
    <StrictMode>
      <BrowserRouter>
        <ErrorBoundary>
          <ShareProvider>
            <App />
          </ShareProvider>
        </ErrorBoundary>
        <Toaster />
      </BrowserRouter>
    </StrictMode>
  );
}

// IP/country + browser locale bootstrap (same logic on mobile and desktop)
const LIGHTWEIGHT_PATHS = new Set(['/download', '/faq', '/terms', '/privacy', '/refund-policy']);

function isLightweightPath(): boolean {
  try {
    const path = window.location.pathname.replace(/\/+$/, '') || '/';
    return LIGHTWEIGHT_PATHS.has(path);
  } catch {
    return false;
  }
}

if (isLightweightPath()) {
  renderApp();
  removeBootSplash();
  void bootstrapLocale();
} else {
  void bootstrapLocale().finally(() => {
    renderApp();
    // CRM login keeps the HTML T splash until CrmLoginPage is ready (no second logo flash).
    if (!shouldKeepBootSplash()) {
      requestAnimationFrame(() => removeBootSplash());
    }
  });
}
